import { isTiersProductVariant } from '@weft/services';
import type { UsageLineItem, TierUsageData, LineItemProductVariant } from '@weft/types';
import { isMultiple, raise, sumBy } from '@weft/utils';
import React, { useCallback } from 'react';
import Table from '../../../../../../components/design/table/Table';
import { useInvoiceCurrency } from '../../../../../../hooks/use-invoice-currency';
import { getProductTitle } from '../../../../../../services/product/product-title';
import type { InvoiceTierUsageTableRow } from '../../../invoice-tiers-usage-table-columns-builder';
import { buildInvoiceTiersUsageTableColumns } from '../../../invoice-tiers-usage-table-columns-builder';
import { InvoiceSectionHeader } from '../../InvoiceSectionHeader';

type InvoiceTiersUsageChargesProps = {
  lineItems: UsageLineItem[]
};

export const InvoiceTiersUsageCharges: React.FC<InvoiceTiersUsageChargesProps> = ({
  lineItems,
}) => {
  const currency = useInvoiceCurrency(lineItems);

  const getColumns = useCallback((units: number) => {
    return buildInvoiceTiersUsageTableColumns(units, currency);
  }, [currency]);

  return (
    <div className="space-y-5">
      {lineItems.map((usageLineItem, index) => (
        <React.Fragment key={index}>
          <div
            className="rounded-2lg border-line border p-5"
          >
            <InvoiceSectionHeader
              className="mb-4"
              name={getProductTitle(usageLineItem.lineItemProduct)}
              description={usageLineItem.lineItemProduct.variants[0].description}
              price={usageLineItem.price}
              currency={currency}
            />
            {usageLineItem.lineItemProduct.variants.map(productVariant => (
              <React.Fragment key={productVariant.productVariantId!}>
                {isMultiple(usageLineItem.lineItemProduct.variants) && (
                  <div
                    className="bg-light-gray px-5 py-2 font-medium text-black"
                  >
                    {productVariant.meter?.name}
                  </div>
                )}
                <div className="p-5">
                  <Table {...{
                    rows: getTiers(productVariant, usageLineItem.tiersUsageMap[productVariant.productVariantId!]),
                    columns: getColumns(sumBy(usageLineItem.tiersUsageMap[productVariant.productVariantId!], 'periodUnits')),
                    tableClassName: 'w-full',
                    headerClassName: 'text-placeholder border-b border-b-line',
                    cellClassName: 'py-1.5',
                  }}
                  />
                </div>
              </React.Fragment>
            ))}
          </div>
        </React.Fragment>
      ))}
    </div>
  );
};

function getTiers(productVariant: LineItemProductVariant, tierUsageData: TierUsageData[]) {
  if (!isTiersProductVariant(productVariant)) {
    return raise('Invalid product variant');
  }

  return productVariant.pricing.priceConfiguration.tiers.map<InvoiceTierUsageTableRow>((tier, index) => ({
    ...tier,
    tierUnits: tierUsageData[index].periodUnits,
  }));
}